import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminEntry } from "@/modules/handbook/admin/types";

export type EntryList = Paginate<AdminEntry<true>>;

export const entries = createQueryKeys("admin-handbook-entries", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "categories";

export const fetchEntriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...entries.all(page, search),
    queryFn: () =>
      adminApi
        .get<EntryList>(`/handbook/entries`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchEntries = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchEntriesQueryOptions(page, search));

export const fetchEntryQueryOptions = (id?: number) =>
  queryOptions({
    ...entries.one(id),
    queryFn: () =>
      adminApi
        .get<AdminEntry<true>>(`/handbook/entries/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchEntry = (id?: number) =>
  useQuery(fetchEntryQueryOptions(id));

export interface EntryRequest {
  title: string;
  excerpt: string;
  content: string;
  status: "draft" | "published" | "disabled";
  action_name: string | null;
  action_url: string | null;
  featured_image: string | null;
  image_thumb?: string | null;
  categories: number[] | string[];
}

export const useCreateEntry = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: EntryRequest) =>
      adminApi
        .post(`/handbook/entries`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: entries.all._def,
      });
    },
  });
};

export const useUpdateEntry = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: EntryRequest) =>
      adminApi
        .put(`/handbook/entries/${id}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: entries.all._def,
      });
      queryClient.invalidateQueries(entries.one(id));
    },
  });
};

export const useCreateOrUpdateEntry = (id: number | undefined) => {
  const create = useCreateEntry();
  const update = useUpdateEntry(id!);
  return id ? update : create;
};

export const useDeleteEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`handbook/entries/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: entries.all._def,
      });
      queryClient.invalidateQueries(entries.one(id));
    },
  });
};
